import _ from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { UndoHistory } from 'utils/undo-history'
import {
  canRedo as historyCanRedo,
  canUndo as historyCanUndo,
  emptyUndoHistory,
  recordUndoEntry,
  redoStep,
  undoStep,
} from 'utils/undo-history'

type SnapshotEntry<K, S> = { key: K; state: S }

export type UseSnapshotHistoryParams<K, S> = {
  /** Comparable value that changes exactly when the page state changes in a
   *  way the user would want to undo. Compared with deep equality. */
  key: K
  /** Captures the full restorable state. Called only when `key` changed. */
  snapshot: () => S
  /** Applies a captured state back onto the page. */
  restore: (state: S) => void
  enabled?: boolean
}

export type UseSnapshotHistoryReturn = {
  canUndo: boolean
  canRedo: boolean
  undo: () => void
  redo: () => void
}

/**
 * Local undo/redo for page state that never reaches the URL (so the hash-based
 * `usePageHistory` can't see it) — saved and unsaved edits alike.
 *
 * Recording is by observation: whenever `key` changes, `snapshot()` is pushed.
 * React batches the several setters of one user action into one render, so one
 * action is one step, and no mutation site needs instrumenting. Undo/redo move
 * the cursor and call `restore`; the next render's `key` equals the entry now
 * at the cursor, so it is amended in place rather than recorded as a new step.
 * History is in-memory for the lifetime of the calling component.
 */
export function useSnapshotHistory<K, S>({
  key,
  snapshot,
  restore,
  enabled = true,
}: UseSnapshotHistoryParams<K, S>): UseSnapshotHistoryReturn {
  const historyRef =
    useRef<UndoHistory<SnapshotEntry<K, S>>>(emptyUndoHistory())
  const [, setVersion] = useState(0)

  const snapshotRef = useRef(snapshot)
  snapshotRef.current = snapshot
  const restoreRef = useRef(restore)
  restoreRef.current = restore

  // Callers often build `key` as a fresh object literal each render. Hold on to
  // the previous identity until the value actually differs (lodash short-
  // circuits on `===`, so an already-memoized key costs nothing), so the effect
  // below fires on real changes only — never once per render.
  const stableKeyRef = useRef(key)
  if (!_.isEqual(stableKeyRef.current, key)) stableKeyRef.current = key
  const stableKey = stableKeyRef.current

  // Re-render only when the buttons' enabled state could have changed.
  const commit = useCallback((next: UndoHistory<SnapshotEntry<K, S>>): void => {
    const flagsChanged =
      historyCanUndo(next) !== historyCanUndo(historyRef.current) ||
      historyCanRedo(next) !== historyCanRedo(historyRef.current)
    historyRef.current = next
    if (flagsChanged) setVersion((version) => version + 1)
  }, [])

  useEffect(() => {
    if (!enabled) return
    commit(
      recordUndoEntry(
        historyRef.current,
        { key: stableKey, state: snapshotRef.current() },
        (current, next) => _.isEqual(current.key, next.key),
      ),
    )
  }, [stableKey, enabled, commit])

  const undo = useCallback((): void => {
    const { history, target } = undoStep(historyRef.current)
    if (!target) return
    commit(history)
    restoreRef.current(target.state)
  }, [commit])

  const redo = useCallback((): void => {
    const { history, target } = redoStep(historyRef.current)
    if (!target) return
    commit(history)
    restoreRef.current(target.state)
  }, [commit])

  return {
    canUndo: enabled && historyCanUndo(historyRef.current),
    canRedo: enabled && historyCanRedo(historyRef.current),
    undo,
    redo,
  }
}
